import { useEffect, useMemo, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { timeLapse, type LapseFrame, type TimeLapse as Lapse } from "@buildlab/codeview";
import type { FileMap } from "@buildlab/shared";
import { api } from "./api";

// Time-lapse: a 30-second "making of" over every saved version, each shown in its live preview with
// what the student asked next. Opened from History ("Time-lapse"). Read-only; Escape closes it.

const STEP_MS = 2500;
const TICK_MS = 100;

export function TimeLapse({ projectId, files, onClose }: { projectId: string; files: FileMap; onClose: () => void }) {
  const [lapse, setLapse] = useState<Lapse | null>(null);
  const [cache, setCache] = useState<Record<number, FileMap>>({});
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0); // ms into the current frame
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(new Set<number>()); // versions already asked for, so none is fetched twice

  useEffect(() => {
    let alive = true;
    api
      .checkpoints(projectId)
      .then(({ checkpoints }) => alive && setLapse(timeLapse(checkpoints, Date.now())))
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [projectId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const total = lapse?.frames.length ?? 0;
  const frame: LapseFrame | undefined = lapse?.frames[index];

  // Load the version on screen and the one after it, so the next frame is ready when it comes.
  useEffect(() => {
    if (!lapse) return;
    for (const f of [lapse.frames[index], lapse.frames[index + 1]]) {
      if (f?.id == null || requested.current.has(f.id)) continue;
      const id = f.id;
      requested.current.add(id);
      api
        .checkpoint(projectId, id)
        .then(({ files: saved }) => setCache((c) => ({ ...c, [id]: saved })))
        .catch((e: Error) => setError(e.message));
    }
  }, [lapse, index, projectId]);

  const shown: FileMap | undefined = frame ? (frame.id == null ? files : cache[frame.id]) : undefined;
  const preview = useMemo(() => (shown ? buildPreview(shown).html : ""), [shown]);

  // The clock waits for the frame to load, and stops at the end card.
  const playing = !!lapse && total > 1 && !paused && !done && !!shown;
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setElapsed((e) => e + TICK_MS), TICK_MS);
    return () => clearInterval(t);
  }, [playing]);

  useEffect(() => {
    if (elapsed < STEP_MS) return;
    if (index < total - 1) {
      setIndex(index + 1);
      setElapsed(0);
    } else {
      setDone(true);
    }
  }, [elapsed]);

  const progress = total ? Math.min(1, (done ? total : index + elapsed / STEP_MS) / total) : 0;
  const started = lapse?.startedAt ? new Date(lapse.startedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  const restart = () => {
    setIndex(0);
    setElapsed(0);
    setDone(false);
    setPaused(false);
  };

  return (
    <div className="lapse" role="dialog" aria-modal="true" aria-label="Time-lapse of your project">
      <div className="lapse-top">
        <span className="eyebrow gold">Time-lapse · the making of your project</span>
        <button type="button" className="linklike" onClick={onClose}>
          Close
        </button>
      </div>

      {error ? (
        <p className="page-error">{error}</p>
      ) : !lapse ? (
        <p className="loading">Loading your history…</p>
      ) : total < 2 ? (
        <p className="lapse-empty">No earlier versions yet. Build something with the helper and the time-lapse will start to fill in.</p>
      ) : (
        <>
          <div className="lapse-stage">
            {shown ? (
              <iframe key={frame?.id ?? "now"} title="This version of your project" sandbox={PREVIEW_SANDBOX} srcDoc={preview} />
            ) : (
              <p className="loading">Loading this version…</p>
            )}
            {done && (
              <div className="lapse-end">
                <h2>
                  {total} versions · from {started} to now
                </h2>
                <p>Every change the helper made, in order.</p>
                <div className="row">
                  <button type="button" className="outline small" onClick={restart}>
                    Watch again
                  </button>
                  <button type="button" className="primary small" onClick={onClose}>
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="lapse-caption">{frame?.caption}</p>
          <div className="lapse-bar" role="progressbar" aria-label="Progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
            <span style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="lapse-controls">
            <span>{index === total - 1 ? "Now" : `Version ${index + 1} of ${total}`}</span>
            {!done && (
              <button type="button" className="outline small" onClick={() => setPaused((p) => !p)}>
                {paused ? "Play" : "Pause"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
